"use client"

import React from 'react'
import Image from 'next/image'
import { cn } from '@/shared/utils/tw'
import type {
  KioskLanguageButtonProps,
  KioskLanguageDropdownProps,
  SupportedKioskLanguage,
} from '../types/kiosk-config'
import { KIOSK_LANGUAGES } from '../types/kiosk-config'

/**
 * 키오스크 언어 버튼 (닫힌 상태)
 *
 * Figma 디자인: node-id=2011-386 (FAQ 버튼)
 *
 * 스타일 (2160px 기준):
 * - 크기: 435x181 (고정)
 * - 배경: rgba(255, 255, 255, 0.8)
 * - backdrop-filter: blur(110.77px)
 * - border-radius: 5532.9px (full pill)
 * - gap: 20px
 * - 이모지: fontSize 100, fontWeight 600
 * - 텍스트: fontSize 70, fontWeight 500, #000000
 */
export const KioskLanguageButton = ({
  selectedLanguage,
  onClick,
}: KioskLanguageButtonProps) => {
  const currentLanguage = KIOSK_LANGUAGES.find(lang => lang.code === selectedLanguage) || KIOSK_LANGUAGES[0]

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        // 고정 크기: 435x181 (드롭다운과 동일한 너비)
        // 435px → 20.14cqw, 181px → 8.38cqw
        'w-[20.14cqw]',
        'h-[8.38cqw]',
        // Glass Effect
        'bg-white/80',
        // backdrop-filter: blur(110.77px)
        'backdrop-blur-[111px]',
        // Pill shape (border-radius: 5532.9px)
        'rounded-full',
        // gap: 20px → 0.93cqw
        'flex items-center justify-center gap-[0.93cqw]',
        // 호버 & 액티브
        'transition-all duration-200',
        'hover:bg-white/90 active:scale-[0.98]',
        'cursor-pointer',
        'pointer-events-auto'
      )}
    >
      {/* 국기 이미지: 90x90 → 4.17cqw */}
      <div className="relative w-[4.17cqw] h-[4.17cqw] shrink-0">
        <Image
          src={currentLanguage.flagImage}
          alt={`${currentLanguage.name} flag`}
          fill
          className="object-contain"
          unoptimized
        />
      </div>
      {/* 텍스트: fontSize 70, fontWeight 500, #000000 → 3.24cqw */}
      <span
        className={cn(
          'font-pretendard font-medium',
          'text-[3.24cqw]',
          'text-black',
          'leading-[1.3]',
          'whitespace-nowrap'
        )}
      >
        {currentLanguage.name}
      </span>
    </button>
  )
}

/**
 * 키오스크 언어 드롭다운 (열린 상태)
 *
 * Figma 디자인 (2026-02-11 업데이트):
 * - Dimmed 배경: rgba(18, 19, 20, 0.6) + 300ms fade-in
 * - 패널 너비: 435 → 516px
 * - 패널 padding: 50 → 82px
 * - 패널 gap: 80 → 66px
 * - 패널 border-radius: 107 → 82px
 * - 패널 border: 8.85px white/20
 * - 국기 이미지 제거 → 텍스트만
 * - 선택 표시: opacity 기반 → 색상 기반 (#000 vs rgba(0,0,0,0.5))
 * - 구분선: 4.94px black/20
 */
export const KioskLanguageDropdown = ({
  selectedLanguage,
  onSelect,
  onClose,
}: KioskLanguageDropdownProps) => {
  return (
    <>
      {/* Dimmed 배경 오버레이 - 300ms fade-in */}
      <div
        className={cn(
          'fixed inset-0 z-[300]',
          'animate-[fadeIn_300ms_ease-out_forwards]'
        )}
        style={{ backgroundColor: 'rgba(18, 19, 20, 0.6)' }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* 드롭다운 패널 - 하단 언어 버튼 바로 위 (Figma: x=1514, y=2364, w=516) */}
      <div
        className={cn(
          'absolute z-[301]',
          // 크기: 516px → 23.89cqw
          'w-[23.89cqw]',
          // Glass Effect
          'bg-white/80',
          // border: 8.85px solid rgba(255,255,255,0.2)
          'border-[0.41cqw] border-white/20',
          'backdrop-blur-[82px]',
          // border-radius: 82px → 3.81cqw
          'rounded-[3.81cqw]',
          // padding: 82px → 3.81cqw
          'p-[3.81cqw]',
          // 그림자
          'shadow-[0_0_50px_rgba(0,0,0,0.1)]',
          'pointer-events-auto'
        )}
        style={{
          right: `${(130 / 2160) * 100}%`,
          top: `${(2364 / 3840) * 100}%`,
        }}
      >
        {/* gap: 66px → 3.05cqw */}
        <div className="flex flex-col gap-[3.05cqw]">
          {KIOSK_LANGUAGES.map((lang, index) => (
            <React.Fragment key={lang.code}>
              <button
                type="button"
                onClick={() => onSelect(lang.code)}
                className={cn(
                  'flex items-center justify-center',
                  // 높이: 92px → 4.26cqw
                  'h-[4.26cqw]',
                  // 호버 효과
                  'transition-all duration-200',
                  'cursor-pointer',
                  'hover:opacity-80'
                )}
              >
                {/* 텍스트만 (국기 제거) - 색상 기반 선택 표시 */}
                <span
                  className={cn(
                    'font-pretendard font-semibold',
                    'text-[3.70cqw]',
                    'leading-[1.3]',
                    'whitespace-nowrap',
                    'transition-colors duration-200'
                  )}
                  style={{
                    color: lang.code === selectedLanguage
                      ? '#000000'
                      : 'rgba(0, 0, 0, 0.5)',
                  }}
                >
                  {lang.name}
                </span>
              </button>

              {/* 구분선 (마지막 항목 제외) - 4.94px, black/20 */}
              {index < KIOSK_LANGUAGES.length - 1 && (
                <div
                  className={cn(
                    'h-[0.23cqw]',
                    'bg-black/20',
                    'w-full'
                  )}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* fadeIn 애니메이션 - global로 선언해야 Tailwind animate-[] 클래스에서 참조 가능 */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  )
}

// 기존 컴포넌트 호환성을 위한 default export 유지
export const KioskLanguageSelector = KioskLanguageButton
export default KioskLanguageButton
